
    import React, { useState, useEffect, useRef } from 'react';
    import { motion, useInView, useAnimation, AnimatePresence } from 'framer-motion';
    import { Menu, X, Instagram, MapPin, Mail, ArrowUp, Phone, Clock } from 'lucide-react';
    import { SparklesCore } from "./components/ui/sparkles";
    import { Helmet } from 'react-helmet-async';

    // --- CONSTANTE DU LOGO ---
    // J'ai remplacé les instructions 'import' pour les assets locaux par des attributions directes
    // d'URL de placeholder. Cela corrige le TypeError lié à l'échec de la résolution
    // des chemins de fichiers dans cet environnement d'exécution (reading 'default' of undefined).
    import  logoUrl from "./assets/lumine_logo.webp";
    import salonBg from "./assets/salon_bg.webp";
    import julie_tattoo from "./assets/julie_tattoo-EDIT.webp"
    import dragon_epee from "./assets/dragon_epee.webp"
    import fleur from "./assets/fleur.webp"
    import gaicha from "./assets/gaicha.webp"
    import memento from "./assets/memento_mori.webp"
    import oeil from "./assets/oeil.webp"
    import oiseau from "./assets/oiseau.webp"
    import serpent from "./assets/serpent.webp"
    import chat from "./assets/chat.webp"
    import soins_kit from "./assets/soins_kit.webp"
    import ScrollMobile from './assets/scroll.webp';


    // Données statiques pour la portfolio (remplacement d'Instagram)
    const galleryImages = [
        { id: 1, src: dragon_epee, alt: "Tatouage Dragon & Epée, Frais" },
        { id: 2, src: fleur, alt: "Tatouage fleur/floral, Frais" },
        { id: 3, src: gaicha, alt: "Tatouage Geisha Japonaise, Frais" },
        { id: 4, src: memento, alt: "Tatouage Réaliste Memento Mori, Frais" },
        { id: 5, src: oeil, alt: "Tatouage Réaliste Oeil, Frais" },
        { id: 6, src: oiseau, alt: "Tatouage Réaliste Oiseau, Frais" },
        { id: 7, src: serpent, alt: "Tatouage Serpent, Frais" },
        { id: 8, src: chat, alt: "Tatouage Chat, Frais" },
    ];


    // --- SEO Metadata and Constants ---
    const SITE_TITLE = "Lumine Tattoo Studio | Tatoueur Wasquehal (Lille)";
    const META_DESCRIPTION = "Lumine Tattoo Studio est un salon de tatouage à Wasquehal (Métropole Lilloise) où bienveillance et savoir-faire se rencontrent. Une tatoueuse expérimentée, passionnée par l’art du trait et l’accompagnement client, vous accueille pour une expérience artistique et humaine unique.";
    const SITE_URL = "https://lumine-tattoo.fr";
    const LOGO_URL = `${SITE_URL}/logo.png`;


    const CONTACT_INFO = {
        address: "9 Rue Francisco Ferrer, 59290 Wasquehal",
        gmapsLink: "https://maps.app.goo.gl/3tupDzt3YMsPgiDy5",
        instagram: "https://www.instagram.com/lumine_tattoostudio/",
        phone: "03 20 12 34 56", // Placeholder Phone
        email: "contact@lumine-tattoo.fr", // Placeholder Email
    };

    const SCHEDULE = [
        { day: "Lundi", hours: "Fermé" },
        { day: "Mardi", hours: "13:00 - 19:00" },
        { day: "Mercredi", hours: "13:00 - 19:00" },
        { day: "Jeudi", hours: "13:00 - 19:00" },
        { day: "Vendredi", hours: "13:00 - 19:00" },
        { day: "Samedi", hours: "13:00 - 19:00" },
        { day: "Dimanche", hours: "Fermé" },
    ];

    // --- Framer Motion Variants ---

    // Variante pour les conteneurs qui apparaissent en fondu et glissent
    const containerVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring",
                damping: 10,
                stiffness: 50,
                staggerChildren: 0.1
            }
        }
    };

    // Variante pour les éléments individuels
    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0 }
    };

    // Variante pour les sections révélées
    const sectionRevealVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
    };

    // --- Composant Lightbox (Modal) ---
    const Lightbox = ({ src, alt, onClose }) => {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center p-4 cursor-pointer"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0.8 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="relative max-w-4xl max-h-[90vh] w-full"
                    onClick={(e) => e.stopPropagation()} // Empêche la fermeture au clic sur l'image
                >
                    <img
                        src={src}
                        alt={alt}
                        className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl"
                    />
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white bg-gray-800/80 p-2 rounded-full hover:bg-gray-700 transition-colors"
                        aria-label="Fermer la Lightbox"
                    >
                        <X size={28} />
                    </button>
                    <p className="mt-4 text-white text-center text-sm italic">{alt}</p>
                </motion.div>
            </motion.div>
        );
    };


    // --- Composant d'Animate-on-Scroll ---
    const FadeIn = ({ children, className = "", sectionId }) => {
        const ref = useRef(null);
        const isInView = useInView(ref, { once: true, margin: "-100px 0px" });
        const controls = useAnimation();

        useEffect(() => {
            if (isInView) {
                controls.start("visible");
            }
        }, [controls, isInView]);

        return (
            <motion.section
                id={sectionId}
                ref={ref}
                initial="hidden"
                animate={controls}
                variants={sectionRevealVariants}
                className={`py-16 md:py-24 ${className}`}
            >
                {children}
            </motion.section>
        );
    };

    // --- Composant Principal de l'Application ---
    export default function App() {
        const [isMenuOpen, setIsMenuOpen] = useState(false);
        const [showScrollTop, setShowScrollTop] = useState(false);
        const [lightbox, setLightbox] = useState(null);

        // Effet pour gérer le bouton "Retour en haut"
        useEffect(() => {
            const handleScroll = () => {
                if (window.scrollY > 300) {
                    setShowScrollTop(true);
                } else {
                    setShowScrollTop(false);
                }
            };

            window.addEventListener('scroll', handleScroll);
            return () => window.removeEventListener('scroll', handleScroll);
        }, []);

const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
        if (window.innerWidth < 1024) { // seulement sur mobile
            const yOffset = -80; // ajuste la valeur selon la hauteur de ton header mobile
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        } else {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setIsMenuOpen(false); // Fermer le menu après la navigation
    }
};

        const handleGalleryClick = (image) => {
            setLightbox(image);
        };

        // --- Rendu ---
return (
    <div className="min-h-screen bg-white text-zinc-900 font-sans">
        <style jsx="true">{`
    * {
        box-sizing: border-box;
    }
    
    html, body {
        max-width: 100vw;
    }
    
    #root {
        overflow-x: clip !important;
    }
    
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&display=swap');
    
    body {
        font-family: 'Inter', sans-serif;
    }
    
    .title-font {
        font-family: 'Playfair Display', serif;
    }
    
    .text-shadow-custom {
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    }
    
    /* Ajout pour le sticky de la section soins */
    @media (min-width: 1024px) {
        .sticky-soins-image {
            position: -webkit-sticky !important;
            position: sticky !important;
            top: 6rem !important;
            align-self: flex-start !important;
        }
    }
`}</style>
                
                {/* SEO Head content - Mieux géré dans un vrai projet, mais inclus pour la sémantique */}
                {/* --- Balises SEO --- */}
    <Helmet>
    <html lang="fr" />
    <title>{SITE_TITLE}</title>
    <meta name="description" content={META_DESCRIPTION} />
    <meta name="keywords" content="tatoueur, tattoo, wasquehal, lille, fine line, blackwork, tatouage, studio tatouage, métropole lilloise" />
    <meta name="robots" content="index, follow" />
    <link rel="canonical" href="https://lumine-tattoo.fr" />
    
    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://lumine-tattoo.fr" />
    <meta property="og:title" content={SITE_TITLE} />
    <meta property="og:description" content={META_DESCRIPTION} />
    <meta property="og:image" content="https://lumine-tattoo.fr/logo.png" />
    <meta property="og:locale" content="fr_FR" />
    
    
    {/* Schema.org JSON-LD */}
    <script type="application/ld+json">
        {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "TattooParlor",
        "name": "Lumine Tattoo Studio",
        "image": "https://lumine-tattoo.fr/logo.png",
        "url": "https://lumine-tattoo.fr",
        "telephone": "06.68.34.65.02",
        "email": "contact@lumine-tattoo.fr",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "9 Rue Francisco Ferrer",
            "addressLocality": "Wasquehal",
            "postalCode": "59290",
            "addressCountry": "FR"
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": "50.669041",
            "longitude": "3.111895"
        },
        "openingHoursSpecification": [
            {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            "opens": "13:00",
            "closes": "19:00"
            }
        ],
        "sameAs": ["https://www.instagram.com/lumine_tattoostudio/"],
        "priceRange": "€€",
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5.0",
            "reviewCount": "50"
        }
        })}
    </script>
    </Helmet>

{/* Barre de Navigation Fixe */}
<header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-md">
    <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-20">
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} // Scroll vers le haut
        >
            <img src={logoUrl} alt="Lumine Tattoo Studio Logo" className="h-10 sm:h-12 object-contain" loading="lazy" />
            <span className="hidden sm:inline font-giaza text-2xl ml-3 -translate-y-1">Lumine Tattoo Studio</span>
        </motion.div>

        {/* Menu Desktop */}
        <div className="hidden lg:flex space-x-8">
            {['accueil', 'studio', 'portfolio', 'rdv','preparation','soins','avis','contact'].map((id) => (
                <motion.button
                    key={id}
                    className="text-zinc-700 hover:text-black font-medium uppercase text-sm tracking-widest transition-colors"
                    onClick={() => scrollToSection(id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    {id.charAt(0).toUpperCase() + id.slice(1)}
                </motion.button>
            ))}

        </div>

        {/* Bouton Menu Mobile */}
        <button 
            className="lg:hidden p-2 text-zinc-700 hover:text-black transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Ouvrir le menu"
        >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
    </nav>
</header>

{/* Menu Mobile (Overlay) */}
<AnimatePresence>
    {isMenuOpen && (
        <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
            className="fixed top-20 left-0 right-0 z-40 bg-white shadow-xl lg:hidden border-t border-gray-100"
        >
            <div className="flex flex-col p-6 space-y-4">
                {['accueil', 'studio', 'portfolio', 'rdv','preparation','soins','avis', 'contact'].map((id) => (
                    <button
                        key={id}
                        className="text-lg font-medium text-zinc-700 hover:text-black text-left py-2 border-b border-gray-100"
                        onClick={() => {
                            scrollToSection(id);
                            setIsMenuOpen(false); // ferme le menu mobile après clic
                        }}
                    >
                        {id.charAt(0).toUpperCase() + id.slice(1)}
                    </button>
                ))}
                <button 
                    className="w-full text-center mt-4 px-4 py-3 bg-black text-white rounded-lg text-lg font-medium hover:bg-indigo-800 transition-colors"
                    onClick={() => {
                        scrollToSection('rdv');
                        setIsMenuOpen(false);
                    }}
                >
                    Prendre Rendez-vous
                </button>
            </div>
        </motion.div>
    )}
</AnimatePresence>
                <main>
{/* Section Accueil / Hero - Version Originale Améliorée */}
    <section 
        id="accueil"
        className="relative pt-20 h-screen flex items-center justify-center text-white overflow-hidden"
    >
        {/* --- Image de fond avec flou doux --- */}
        <div className="absolute inset-0">
            <img 
                src={salonBg}
                alt="Intérieur du studio de tatouage Lumine"
                className="w-full h-full object-cover filter blur-[6px] scale-105"
                loading="lazy"
            />
            {/* Overlay sombre pour contraste avec gradient subtil */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/65 to-black/75"></div>
        </div>

        {/* --- Effet de particules lumineuses limité à la section --- */}
        <div className="absolute inset-0 z-[2] overflow-hidden pointer-events-none">
            <div className="relative w-full h-full">
                <SparklesCore
                    id="sparkles-lumine"
                    background="transparent"
                    minSize={1}
                    maxSize={3}
                    particleDensity={50}
                    particleColor="#ffffff"
                    className="absolute inset-0 w-full h-full"
                    style={{ position: "absolute", top: 0, left: 0 }}
                />
            </div>
        </div>

        {/* --- Logo en filigrane derrière le texte --- */}
        <motion.img
            src={logoUrl}
            alt="Logo Lumine Tattoo Studio"
            initial={{ opacity: 0, scale: 0.75 }}
            animate={{ opacity: 0.25, scale: 0.85 }}
            transition={{ duration: 1.4, delay: 0.8, ease: "easeOut" }}
            className="absolute inset-0 m-auto w-[80vw] max-w-[950px] h-auto pointer-events-none select-none filter blur-sm"
            style={{ zIndex: 3 }}
        />

        {/* --- Contenu principal (texte + boutons) --- */}
        <div className="relative z-[5] text-center max-w-5xl px-6">
            {/* Badge animé au-dessus du titre - Version plus épurée */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="inline-block mb-8"
            >
                <span className="px-6 py-2 bg-white/5 backdrop-blur-md border border-white/20 rounded-full text-xs font-light tracking-[0.3em] uppercase text-white/90">
                    Wasquehal • Métropole Lilloise
                </span>
            </motion.div>

            {/* Titre principal - Espacement amélioré */}
            <motion.h1
                className="mb-12 text-shadow-custom leading-tight"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
            >
                <span className="block font-giaza text-[15vw] sm:text-[12vw] md:text-8xl lg:text-9xl tracking-tight">
                    Lumine
                </span>
                <span className="block font-giaza text-[13vw] sm:text-[10vw] md:text-7xl lg:text-8xl mt-2 tracking-tight">
                    Tattoo
                </span>
                
                {/* Séparateur minimaliste */}
                <div className="flex items-center justify-center gap-4 my-6">
                    <div className="h-[1px] w-16 bg-white/30"></div>
                    <span className="text-xs md:text-sm tracking-[0.4em] text-white/70 font-light">
                        STUDIO
                    </span>
                    <div className="h-[1px] w-16 bg-white/30"></div>
                </div>
                
                <span className="block text-xs md:text-sm mt-3 text-white/50 tracking-widest font-light">
                    Since 2019
                </span>
            </motion.h1>

            {/* Groupe de boutons CTA - Design plus épuré */}
            <motion.div
                className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9 }}
            >
<motion.button
    onClick={() => scrollToSection('rdv')}
    className="group relative px-10 py-3.5 bg-white text-black text-sm font-medium tracking-widest overflow-hidden hover:bg-indigo-600"
    whileHover={{ scale: 1.03, y: -2 }}
    whileTap={{ scale: 0.98 }}
    style={{
        transition: 'background-color 0.3s ease'
    }}
>
    <span className="relative z-10 flex items-center gap-3 group-hover:text-white transition-colors">
        PRENDRE RENDEZ-VOUS
        <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
    </span>
</motion.button>

                <motion.button
                    onClick={() => scrollToSection('portfolio')}
                    className="px-10 py-3.5 bg-transparent backdrop-blur-sm border border-white/30 text-white text-sm font-medium tracking-widest hover:bg-white/10 transition-all duration-300"
                    whileHover={{ scale: 1.03, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                >
                    PORTFOLIO
                </motion.button>
            </motion.div>

            {/* Informations pratiques - Plus espacé et épuré */}
            <motion.div
                className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/60"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.2 }}
            >
                <span className="flex items-center gap-2 font-light">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                    Mardi - Samedi • 13h - 19h
                </span>
                <span className="hidden sm:inline text-white/30">|</span>
                <span className="flex items-center gap-2 font-light">
                    <MapPin size={16} className="opacity-70" />
                    9 Rue Francisco Ferrer, Wasquehal
                </span>
            </motion.div>
        </div>

        {/* Indicateur de scroll animé - Plus minimaliste */}
        <motion.div
            className="absolute bottom-8 left-0 right-0 z-[5] flex justify-center"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
                duration: 0.8, 
                delay: 1.5,
                repeat: Infinity,
                repeatType: "reverse",
                repeatDelay: 0.5
            }}
        >
            {/* Version Desktop / souris */}
            <div 
                className="hidden md:flex flex-col items-center gap-2 text-white/50 cursor-pointer hover:text-white/70 transition-colors" 
                onClick={() => scrollToSection('studio')}
            >
                <span className="text-[10px] uppercase tracking-[0.3em] font-light">Découvrir</span>
                <div className="w-6 h-10 border border-white/30 rounded-full flex justify-center pt-2">
                    <div className="w-1 h-2 bg-white/50 rounded-full animate-bounce"></div>
                </div>
            </div>

            {/* Version Mobile / image */}
            <div 
                className="flex md:hidden flex-col items-center gap-2 text-white/50 cursor-pointer" 
                onClick={() => scrollToSection('studio')}
            >
                <img 
                    src={ScrollMobile} 
                    alt="Indicateur scroll" 
                    className="w-6 h-10 object-contain opacity-70" 
                />
            </div>
        </motion.div>

        {/* Accents décoratifs minimalistes dans les coins */}
        <div className="absolute top-8 left-8 w-12 h-12 border-l border-t border-white/10"></div>
        <div className="absolute bottom-8 right-8 w-12 h-12 border-r border-b border-white/10"></div>
    </section>

<div className="relative" style={{ 
    background: 'linear-gradient(to bottom right, rgb(224, 242, 254), rgb(245, 239, 255), rgb(253, 238, 245))',
    overflow: 'visible' 
}}>

    {/* Fond animé avec blobs */}
    <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-indigo-300/30 to-purple-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-br from-purple-300/30 to-pink-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-br from-pink-300/30 to-indigo-300/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
    </div>
                    {/* Section Studio/Philosophie avec Liquid Glass */}
    <FadeIn sectionId="studio" className="relative overflow-hidden">

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* En-tête */}
            <div className="text-center mb-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <span className="inline-block px-4 py-2 bg-white/70 backdrop-blur-xl text-indigo-700 rounded-full text-sm font-semibold uppercase tracking-wider mb-4 border border-white/80">
                        Notre ADN
                    </span>
                    <h2 className="title-font text-4xl md:text-5xl font-bold text-zinc-900 mb-6">
                        Notre Philosophie
                    </h2>
                    <p className="text-xl text-zinc-600 max-w-3xl mx-auto mb-4">
                        Chez Lumine, le tatouage va au-delà de l'encre et de la peau.
                    </p>
                    <p className="text-lg text-zinc-500 italic max-w-2xl mx-auto">
                        "Chaque trait raconte une histoire, chaque rendez-vous est une rencontre."
                    </p>
                </motion.div>
            </div>
            
            {/* Cartes avec effet Liquid Glass */}
            <motion.div 
                className="grid grid-cols-1 md:grid-cols-3 gap-8"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
                {[
                    { 
                        title: "Bienveillance", 
                        icon: "💙",
                        gradient: "from-blue-500 to-indigo-600",
                        desc: "Chaque projet est traité avec le plus grand respect et une écoute attentive de vos besoins. Ton confort et ta sérénité sont ma priorité.",
                        keywords: ["Écoute active", "Respect", "Confiance"]
                    },
                    { 
                        title: "Hygiène & Sécurité", 
                        icon: "✨",
                        gradient: "from-purple-500 to-pink-600",
                        desc: "Normes d'hygiène strictes, matériel stérilisé et protocoles certifiés pour garantir une expérience en toute sécurité.",
                        keywords: ["Stérilisation", "Équipement pro", "Normes"]
                    },
                    { 
                        title: "Création Unique", 
                        icon: "🎨",
                        gradient: "from-pink-500 to-orange-500",
                        desc: "Des designs personnalisés et travaillés avec soin, du Fine Line au Blackwork, pour une œuvre qui te ressemble vraiment.",
                        keywords: ["Sur-mesure", "Fine Line", "Blackwork"]
                    },
                ].map((item, index) => (
                    <motion.div 
                        key={index} 
                        variants={itemVariants}
                        whileHover={{ y: -10, scale: 1.02 }}
                        className="relative bg-white/60 backdrop-blur-2xl p-8 rounded-3xl shadow-2xl border border-white/80 overflow-hidden group hover:border-white transition-all duration-300"
                        style={{ 
                            boxShadow: '0 8px 32px 0 rgba(99, 102, 241, 0.2), inset 0 1px 0 0 rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(20px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                        }}
                    >
                        {/* Reflet supérieur */}
                        <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent rounded-t-3xl pointer-events-none"></div>
                        
                        {/* Icône flottante avec effet glass */}
                        <motion.div 
                            className={`relative w-16 h-16 mb-6 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center text-3xl shadow-lg`}
                            whileHover={{ rotate: 10, scale: 1.1 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent rounded-2xl"></div>
                            <span className="relative z-10">{item.icon}</span>
                        </motion.div>

                        <h3 className="relative text-2xl font-bold title-font mb-4 text-zinc-900">
                            {item.title}
                        </h3>
                        
                        <p className="relative text-zinc-600 leading-relaxed mb-6">
                            {item.desc}
                        </p>

                        {/* Tags */}
                        <div className="relative flex flex-wrap gap-2">
                            {item.keywords.map((keyword, idx) => (
                                <span 
                                    key={idx}
                                    className="px-3 py-1 bg-white/50 backdrop-blur-sm text-zinc-700 rounded-full text-xs font-medium border border-white/60"
                                >
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    </motion.div>
                ))}
            </motion.div>

            {/* Citation de fermeture avec effet glass */}
            <motion.div
                className="mt-16 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                viewport={{ once: true }}
            >
                <div className="relative inline-block max-w-3xl p-8 bg-white/70 backdrop-blur-2xl rounded-3xl border border-white/90 shadow-2xl"
                    style={{ 
                        boxShadow: '0 8px 32px 0 rgba(99, 102, 241, 0.15), inset 0 1px 0 0 rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(20px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                    }}
                >
                    <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent rounded-t-3xl pointer-events-none"></div>
                    <p className="relative text-lg text-zinc-700 italic leading-relaxed mb-4">
                        "Chez Lumine, je ne tatoue pas seulement : je construis avec toi une œuvre qui t'accompagnera toute ta vie."
                    </p>
                    <div className="relative flex items-center justify-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                            <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full"></div>
                            <span className="relative z-10">J</span>
                        </div>
                        <div className="text-left">
                            <p className="font-semibold text-zinc-900">Julie</p>
                            <p className="text-sm text-zinc-600">Fondatrice & Tatoueuse</p>
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    </FadeIn>
                  {/* Section Artiste (Julie) */}
                    <FadeIn sectionId="artiste" className="relative overflow-hidden">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                                {/* Image de l'Artiste */}
                                <motion.div
                                    initial={{ opacity: 0, x: -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.7 }}
                                    viewport={{ once: true, amount: 0.5 }}
                                >
                                    <img
                                        src={julie_tattoo}
                                        alt="Portrait de Julie, tatoueuse chez Lumine Studio"
                                        className="w-full h-auto object-cover rounded-2xl shadow-2xl aspect-[4/5] lg:aspect-[3/4]"
                                        loading="lazy"
                                    />
                                </motion.div>

                                {/* Bio de l'Artiste */}
                                <motion.div
                                    initial={{ opacity: 0, x: 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.7, delay: 0.2 }}
                                    viewport={{ once: true, amount: 0.5 }}
                                >
                                    <span className="text-indigo-600 font-semibold uppercase tracking-wider mb-2 block">L'Artiste</span>
                                    <h2 className="title-font text-4xl font-bold text-zinc-900 mb-6">Rencontrez Julie</h2>
                                    <p className="text-lg text-zinc-700 mb-6 leading-relaxed">
                                        Passionnée par le dessin et l'art corporel, Julie est la fondatrice de Lumine Tattoo Studio. Son style de prédilection se concentre sur le *Fine Line* délicat, le *Blackwork* graphique et les créations florales, mais elle excelle à donner vie à toute idée originale.
                                    </p>
                                    <p className="text-lg text-zinc-700 mb-8 leading-relaxed">
                                        Pour Julie, le tatouage est avant tout une collaboration. Elle met un point d'honneur à créer une connexion de confiance avec chaque client, s'assurant que le design final reflète parfaitement l'intention et l'histoire de la personne.
                                    </p>
                                    <motion.a
                                        href={CONTACT_INFO.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-full text-md font-semibold hover:bg-indigo-700 transition-colors shadow-lg"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <Instagram size={20} className="mr-2" />
                                        Voir son Instagram
                                    </motion.a>
                                </motion.div>
                            </div>
                        </div>
                    </FadeIn>

   {/* Section Portfolio avec Liquid Glass */}
    <FadeIn sectionId="portfolio" className="relative overflow-hidden">


        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* En-tête avec effet glass */}
            <div className="text-center mb-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <span className="inline-block px-4 py-2 bg-white/70 backdrop-blur-xl text-indigo-700 rounded-full text-sm font-semibold uppercase tracking-wider mb-4 border border-white/80">
                        Portfolio
                    </span>
                    <h2 className="title-font text-4xl md:text-5xl font-bold text-zinc-900 mb-4">
                        Nos Réalisations
                    </h2>
                    <p className="text-xl text-zinc-600 max-w-2xl mx-auto mb-8">
                        Chaque tatouage raconte une histoire unique. Découvrez quelques créations récentes du studio.
                    </p>
                </motion.div>

                {/* Mini stats avec effet glass */}
                <motion.div 
                    className="flex flex-wrap justify-center gap-8 mt-8"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                >
                    {[
                        { label: "Projets réalisés", value: "1000+" },
                        { label: "Années d'expérience", value: "6+" },
                        { label: "Clients satisfaits", value: "100%" }
                    ].map((stat, index) => (
                        <div key={index} className="relative text-center bg-white/60 backdrop-blur-2xl px-8 py-4 rounded-3xl border border-white/80 shadow-xl"
                            style={{ 
                                boxShadow: '0 4px 16px 0 rgba(99, 102, 241, 0.1), inset 0 1px 0 0 rgba(255, 255, 255, 0.8)',
                                backdropFilter: 'blur(20px) saturate(180%)',
                                WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                            }}
                        >
                            <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent rounded-t-3xl pointer-events-none"></div>
                            <div className="relative text-3xl font-bold title-font text-indigo-600">{stat.value}</div>
                            <div className="relative text-sm text-zinc-600 mt-1">{stat.label}</div>
                        </div>
                    ))}
                </motion.div>
            </div>

            {/* Grille d'images - on garde le style original car les images sont déjà visuelles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {galleryImages.map((image, index) => (
                    <motion.div
                        key={image.id}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        viewport={{ once: true, amount: 0.1 }}
                        onClick={() => handleGalleryClick(image)}
                        className={`relative overflow-hidden rounded-2xl shadow-lg cursor-pointer group ${
                            index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                        } ${index === 3 ? 'md:col-span-2' : ''}`}
                    >
                        <motion.img
                            src={image.src}
                            alt={image.alt}
                            className="w-full h-full object-cover aspect-square"
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out flex flex-col justify-end p-6">
                            <motion.img
                                src={logoUrl}
                                alt="Logo"
                                className="absolute top-4 right-4 w-8 h-8 opacity-70"
                                initial={{ opacity: 0, rotate: -10 }}
                                whileHover={{ opacity: 1, rotate: 0 }}
                                transition={{ duration: 0.3 }}
                            />
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                whileHover={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                            >
                                <h3 className="text-white font-semibold text-lg mb-1">{image.alt}</h3>
                                <p className="text-white/80 text-sm flex items-center gap-2">
                                    <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                                    Voir en détail
                                </p>
                            </motion.div>
                        </div>
                        <div className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-zinc-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            #{index + 1}
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* CTA Instagram avec effet glass */}
            <motion.div 
                className="text-center mt-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
            >
                <div className="inline-block p-1 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-full">
                    <a
                        href={CONTACT_INFO.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-8 py-4 bg-white/90 backdrop-blur-xl rounded-full text-lg font-semibold hover:bg-white transition-all duration-300 group"
                    >
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 rounded-full flex items-center justify-center">
                            <Instagram size={20} className="text-white" />
                        </div>
                        <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
                            Découvre tout le portfolio sur Instagram
                        </span>
                        <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </a>
                </div>
                <p className="text-sm text-zinc-500 mt-4">
                    Plus de 1000 créations • Stories quotidiennes • Disponibilités en temps réel
                </p>
            </motion.div>
        </div>
    </FadeIn>

{/* Section RDV - Avec mention Préparation */}
    <FadeIn sectionId="rdv" className="relative overflow-hidden">

        
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
        <h2 className="title-font text-4xl font-bold text-zinc-900 mb-3">Prise de Rendez-vous</h2>
        <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
            Un processus simple en 3 étapes pour concrétiser ton projet de tatouage.
        </p>
        </div>

        {/* Timeline des étapes */}
        <div className="relative max-w-5xl mx-auto mb-16">
        {/* Ligne de progression (cachée sur mobile) */}
        <div className="hidden md:block absolute top-20 left-0 right-0 h-1 bg-gradient-to-r from-indigo-200 via-indigo-400 to-indigo-600 mx-auto" style={{ width: 'calc(100% - 200px)', left: '100px' }}></div>
        
        <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
        >
            {[
            { 
                title: "1. Partage tes envies",
                icon: "💭",
                color: "from-indigo-500 to-indigo-600",
                desc: [
                "Envoie-moi en message privé sur Instagram tes photos de référence et tes idées.",
                "Précise la zone du corps et la taille approximative souhaitée."
                ]
            },
            { 
                title: "2. Reçois ton devis",
                icon: "📋",
                color: "from-purple-500 to-purple-600",
                desc: [
                "J'étudie ton projet avec attention et te propose un devis personnalisé sous 48h.",
                "Une fois validé, nous fixons ensemble la date et l'heure de ta séance."
                ]
            },
            { 
                title: "3. Confirme ta séance",
                icon: "✅",
                color: "from-pink-500 to-pink-600",
                desc: [
                "Un acompte de 50€ minimum est demandé pour réserver ton créneau.",
                "Tu recevras toutes les infos pratiques pour bien préparer ta venue."
                ]
            },
            ].map((item, index) => (
            <motion.div 
                key={index} 
                variants={itemVariants}
                whileHover={{ y: -8 }}
                className="relative bg-white p-8 rounded-2xl shadow-xl border border-gray-100 flex flex-col"
            >
                {/* Badge numéro avec icône */}
                <div className={`absolute -top-6 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-gradient-to-br ${item.color} rounded-full flex items-center justify-center text-3xl shadow-lg ring-4 ring-white`}>
                {item.icon}
                </div>
                
                <div className="mt-8 flex-1">
                <h3 className="text-xl font-bold title-font mb-4 text-zinc-900 text-center">
                    {item.title}
                </h3>
                <div className="space-y-3">
                    {item.desc.map((line, idx) => (
                    <div key={idx} className="flex items-start">
                        <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <p className="text-zinc-600 leading-relaxed text-left">{line}</p>
                    </div>
                    ))}
                </div>
                </div>
            </motion.div>
            ))}
        </motion.div>
        </div>

        {/* CTA - Deux boutons côte à côte */}
        <div className="text-center mb-8">
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.a
            href={CONTACT_INFO.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-lg font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            >
            <Instagram size={24} className="mr-3" />
            Prendre rendez-vous sur Instagram
            </motion.a>

            <motion.a
            href="mailto:contact@lumine-tattoo.fr"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full text-lg font-semibold hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            >
            <Mail size={24} className="mr-3" />
            Envoyer un email
            </motion.a>
        </div>

        {/* Mentions sous les boutons */}
        <motion.div
            className="mt-6 space-y-2"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
        >
            <p className="text-zinc-600 flex items-center justify-center gap-2">
            <Phone size={18} className="text-indigo-600" />
            Ou appelez au <a href="tel:0668346502" className="font-semibold text-indigo-700 hover:text-indigo-800 transition-colors">06.68.34.65.02</a>
            </p>
            <p className="text-zinc-600 flex items-center justify-center gap-2">
            <MapPin size={18} className="text-indigo-600" />
            Vous pouvez également venir directement au shop pour discuter de votre projet !
            </p>
        </motion.div>
        </div>

        {/* Nouveau bloc : Rendez-vous confirmé */}
        <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="relative max-w-4xl mx-auto mb-12"
        >
        <div className="bg-white/40 backdrop-blur-xl border-2 border-white/60 rounded-2xl p-8 shadow-xl transition-all duration-300"
            style={{ 
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)'
            }}
        >
            <div className="flex items-start gap-6">
            <div className="flex-shrink-0 w-16 h-16 bg-emerald-400/90 backdrop-blur-md rounded-full flex items-center justify-center text-3xl shadow-lg border-2 border-white/50">
                ✓
            </div>
            <div className="flex-1">
                <h4 className="text-xl font-bold text-zinc-900 mb-4 title-font">
                Ton rendez-vous est confirmé ? Prépare ta séance !
                </h4>
                <p className="text-zinc-800 leading-relaxed mb-4">
                Maintenant que ton rendez-vous est pris, consulte notre section <span className="font-semibold">Préparation</span> pour découvrir tous les conseils essentiels avant ta venue au studio.
                </p>
                <button
                onClick={() => scrollToSection('preparation')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-full font-semibold hover:bg-emerald-700 transition-colors"
                >
                Voir les conseils de préparation
                <span>→</span>
                </button>
            </div>
            </div>
        </div>
        </motion.div>

        {/* Bloc conditions d'annulation */}
        <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="relative max-w-4xl mx-auto"
        >
        <div className="bg-white/40 backdrop-blur-xl border-2 border-white/60 rounded-2xl p-8 shadow-xl"
            style={{ 
                boxShadow: '0 8px 32px 0 rgba(251, 146, 60, 0.2)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)'
            }}
        >
            <div className="flex items-start gap-6">
            <div className="flex-shrink-0 w-16 h-16 bg-amber-400/90 backdrop-blur-md rounded-full flex items-center justify-center text-3xl shadow-lg border-2 border-white/50">
                ⚠️
            </div>
            <div className="flex-1">
                <h4 className="text-xl font-bold text-zinc-900 mb-4 title-font">
                Politique d'annulation
                </h4>
                <div className="space-y-3 text-zinc-800">
                <div className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <p className="leading-relaxed">
                    <span className="font-semibold">Délai de 72h :</span> Les reports ou annulations doivent être effectués au moins 72 heures avant la séance.
                    </p>
                </div>
                <div className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <p className="leading-relaxed">
                    <span className="font-semibold">Annulation tardive :</span> Passé ce délai, l'acompte versé ne pourra être remboursé.
                    </p>
                </div>
                <div className="flex items-start">
                    <span className="inline-block w-2 h-2 bg-amber-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <p className="leading-relaxed">
                    <span className="font-semibold">Avoir valable :</span> En cas d'annulation dans les délais, un avoir d'un an te sera proposé.
                    </p>
                </div>
                </div>
            </div>
            </div>
        </div>
        </motion.div>
    </div>
    </FadeIn>

    {/* NOUVELLE Section Préparation */}
    <FadeIn sectionId="preparation" className="relative overflow-hidden">

        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold uppercase tracking-wider mb-4">
                        Avant ta séance
                    </span>
                    <h2 className="title-font text-4xl md:text-5xl font-bold text-zinc-900 mb-4">
                        Comment Bien Préparer ta Séance
                    </h2>
                    <p className="text-xl text-zinc-600 max-w-3xl mx-auto">
                        Ton rendez-vous est maintenant fixé : félicitations ! Voici quelques conseils essentiels pour bien préparer ta venue et faciliter la cicatrisation de ton futur tatouage.
                    </p>
                </motion.div>
            </div>

            {/* Grille de conseils */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {[
                    {
                        icon: "☀️",
                        title: "Évite toute exposition au soleil",
                        desc: "Si tu prends un coup de soleil sur la zone à tatouer, je ne pourrai malheureusement pas te tatouer et nous devrons reporter la séance.",
                        color: "from-orange-500 to-red-500"
                    },
                    {
                        icon: "🚫",
                        title: "N'utilise pas de crème anesthésiante",
                        desc: "Elle peut modifier la texture de ta peau : non seulement cela rend le tatouage plus difficile, mais cela peut aussi altérer le tissu cutané et déformer le résultat final.",
                        color: "from-red-500 to-pink-500"
                    },
                    {
                        icon: "🍺",
                        title: "Pas d'alcool ni de substances",
                        desc: "Ces produits peuvent augmenter le saignement, te rendre malade pendant la séance et accentuer la douleur.",
                        color: "from-purple-500 to-indigo-500"
                    },
                    {
                        icon: "💧",
                        title: "Hydrate ta peau avant de venir",
                        desc: "Idéalement, applique une crème hydratante chaque jour : plus tu commences tôt, plus ta peau sera facile à tatouer.",
                        color: "from-blue-500 to-cyan-500"
                    },
                    {
                        icon: "🍽️",
                        title: "Mange et dors suffisamment",
                        desc: "Arriver reposé et avec le ventre plein est essentiel. Ton corps a besoin d'énergie pour tenir tout au long de la séance. Tu peux également apporter des snacks et des boissons sucrées.",
                        color: "from-green-500 to-emerald-500"
                    },
                    {
                        icon: "👕",
                        title: "Habille-toi confortablement",
                        desc: "Même si je fais attention, il est possible que tes vêtements soient tachés. Privilégie donc une tenue confortable, adaptée à plusieurs heures d'immobilité, et que tu n'as pas peur de tâcher. Si tu es pudique, pense à choisir une tenue adaptée à la zone à tatouer.",
                        color: "from-indigo-500 to-purple-500"
                    },
                    {
                        icon: "📱",
                        title: "Prends de quoi t'occuper",
                        desc: "Un livre, tes écouteurs… Fais comme tu préfères ! C'est ta séance : l'important est que tu sois à l'aise et que tu puisses te changer les idées si nécessaire.",
                        color: "from-pink-500 to-rose-500"
                    }
                ].map((conseil, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        whileHover={{ y: -5 }}
                        className="bg-white/40 backdrop-blur-xl p-6 rounded-2xl shadow-xl border border-white/60 hover:bg-white/50 transition-all duration-300"
                        style={{ 
                            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
                            backdropFilter: 'blur(12px)',
                            WebkitBackdropFilter: 'blur(12px)'
                        }}
                    >
                        {/* Icône avec gradient et effet glass */}
                        <div className={`w-14 h-14 bg-gradient-to-br ${conseil.color} rounded-xl flex items-center justify-center text-3xl shadow-lg mb-4 backdrop-blur-sm border border-white/30`}>
                            {conseil.icon}
                        </div>

                        <h3 className="text-xl font-bold text-zinc-900 mb-3 title-font">
                            {conseil.title}
                        </h3>

                        <p className="text-zinc-700 leading-relaxed">
                            {conseil.desc}
                        </p>
                    </motion.div>
                ))}
            </div>

            {/* Message final encourageant */}
            <motion.div 
                className="text-center max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
            >
                <div className="bg-white/50 backdrop-blur-xl p-8 rounded-2xl border border-white/60 shadow-xl"
                    style={{ 
                        boxShadow: '0 8px 32px 0 rgba(99, 102, 241, 0.15)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)'
                    }}
                >
                    <p className="text-lg text-zinc-700 leading-relaxed mb-4">
                        En suivant ces conseils, tu mettras toutes les chances de ton côté pour une séance réussie et une belle cicatrisation ! 💜
                    </p>
                    <p className="text-base text-zinc-600 italic">
                        Si tu as des questions, n'hésite pas à me contacter avant ton rendez-vous.
                    </p>
                </div>
            </motion.div>
        </div>
    </FadeIn>

{/* Section Soins */}
<FadeIn sectionId="soins" className="relative">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
            <h2 className="title-font text-4xl font-bold text-zinc-900 mb-3">
                Prendre Soin de Ton Tattoo
            </h2>
            <p className="text-xl text-zinc-600 max-w-3xl mx-auto">
                Félicitations, ton rendez-vous est pris ! Maintenant, découvre comment préserver au mieux ton œuvre d'art corporelle.
            </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Colonne Image du Kit - STICKY */}
            <div className="sticky-soins-image">
                <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                        <img
                            src={soins_kit}
                            alt="Kit de soins pour tatouage disponible chez Lumine Tattoo Studio"
                            className="w-full h-auto object-cover aspect-square"
                            loading="lazy"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                            <p className="text-white text-lg font-semibold">Kits de soins disponibles en magasin</p>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Colonne Instructions */}
            <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                viewport={{ once: true, amount: 0.3 }}
                className="space-y-6"
            >
                {/* Message introductif */}
                <div className="bg-indigo-50 border-l-4 border-indigo-600 p-5 rounded-lg">
                    <p className="text-zinc-800 leading-relaxed">
                        <span className="font-semibold">Note importante :</span> Chaque tatoueur a ses propres méthodes. Ces consignes peuvent varier selon le projet et la zone tatouée. Voici mes recommandations générales.
                    </p>
                </div>

                {/* Liste des étapes de soin */}
                <div className="space-y-5">
                    {[
                        {
                            titre: "Retrait du film protecteur",
                            desc: "Retire le film après 1 à 2 heures, mains propres. Nettoie délicatement à l'eau claire avec un savon ou huile pH neutre, puis sèche en tamponnant avec un essuie-tout ou linge propre."
                        },
                        {
                            titre: "Si le tatouage continue de dégorger",
                            desc: "Applique un nouveau pansement avec une fine couche de vaseline et du film protecteur. Une fois le dégorgement terminé, passe aux étapes suivantes."
                        },
                        {
                            titre: "Attendre avant l'hydratation",
                            desc: "Laisse ton tatouage respirer pendant au moins 24h avant d'appliquer une crème, afin de permettre à la cicatrisation de débuter correctement."
                        },
                        {
                            titre: "Hydratation quotidienne",
                            desc: "Après t'être lavé les mains, applique une fine couche de crème Aquaphor ou de vaseline 2 à 3 fois par jour pendant 8 à 10 jours. Les zones sensibles (mains) nécessitent une hydratation plus fréquente."
                        },
                        {
                            titre: "Ne pas gratter les croûtes",
                            desc: "Pendant 4 semaines, ne gratte jamais les croûtes : environ 70% des pigments s'y trouvent, essentiels pour le résultat final."
                        },
                        {
                            titre: "Éviter l'eau et les UV",
                            desc: "Pas de bain, sauna, piscine, ni d'exposition aux UV (soleil, banc solaire) pendant la cicatrisation. Ne laisse pas ton tatouage tremper ou macérer."
                        },
                        {
                            titre: "Visite de contrôle",
                            desc: "Reviens me voir un mois après la séance pour évaluer l'évolution et déterminer si une retouche est nécessaire."
                        }
                    ].map((etape, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true, amount: 0.3 }}
                            className="bg-gray-50 p-5 rounded-xl border border-gray-200 hover:border-indigo-300 transition-colors"
                        >
                            <h3 className="font-semibold text-lg text-zinc-900 mb-2 flex items-start">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold mr-3 flex-shrink-0 mt-0.5">
                                    {index + 1}
                                </span>
                                {etape.titre}
                            </h3>
                            <p className="text-zinc-700 leading-relaxed ml-10">{etape.desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* CTA Kit de soins */}
                <motion.div 
                    className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 p-6 rounded-xl text-white shadow-xl"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                >
                    <h4 className="font-bold text-xl mb-2">Besoin d'un kit de soins ?</h4>
                    <p className="mb-4 opacity-90">
                        Des kits complets sont disponibles directement au studio avec tous les produits recommandés.
                    </p>
                    <button 
                        onClick={() => scrollToSection('contact')}
                        className="bg-white text-indigo-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
                    >
                        Nous contacter
                    </button>
                </motion.div>
            </motion.div>
        </div>
    </div>
</FadeIn>

    {/* Section Avis Google */}
    <FadeIn sectionId="avis" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* En-tête */}
            <div className="text-center mb-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold uppercase tracking-wider mb-4">
                        Témoignages
                    </span>
                    <h2 className="title-font text-4xl md:text-5xl font-bold text-zinc-900 mb-4">
                        Ce Que Disent Nos Clients
                    </h2>
                    <p className="text-xl text-zinc-600 max-w-2xl mx-auto mb-6">
                        La satisfaction de nos clients est notre plus belle récompense.
                    </p>
                    
                    {/* Note Google */}
                    <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-3 rounded-full border border-yellow-200">
                        <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                                <span key={i} className="text-yellow-400 text-xl">★</span>
                            ))}
                        </div>
                        <span className="text-2xl font-bold text-zinc-900">5.0</span>
                        <span className="text-zinc-600">sur Google</span>
                    </div>
                </motion.div>
            </div>

            {/* Grille d'avis */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                    {
                        name: "Delphine C.",
                        rating: 5,
                        text: "Résultat exceptionnel ! ✨\n\nLe tatouage est fidèle à 100% au modèle présenté 😍\n\nLa tatoueuse a fait preuve d'une grande disponibilité pour écouter ma demande et nous avons pu discuter longuement de tous les détails du projet.\n\nMalgré la petite taille du tatouage, elle n'a rien négligé et a consacré tout le temps nécessaire pour un travail parfait.\n\nProfessionnalisme, propreté, écoute attentive... Je recommande sans hésitation ! 🔥\n\nEncore merci pour ce magnifique travail 🙏🖤",
                        avatar: "D"
                    },
                    {
                        name: "Kléa B.",
                        rating: 5,
                        text: "Incroyable !\n\nTrois tatouages réalisés à la perfection, tatoueuse d'une gentillesse exceptionnelle.\n\nDe bons moments qui respectent tous les points cruciaux… hygiène, relationnel et artistique !\n\nJe recommande ++",
                        avatar: "K"
                    },
                    {
                        name: "Jade G.",
                        rating: 5,
                        text: "Merci à Julie pour son accueil, sa gentillesse et le travail bien fait.\n\nNous nous sommes fait un petit tatouage en famille et nous sommes ravis du résultat.\n\nC'est une adresse qu'on recommande à 200% !",
                        avatar: "J"
                    },
                    {
                        name: "Cassiopée R.",
                        rating: 5,
                        text: "Deuxième fois pour moi, et loin d'être la dernière, j'ai encore une fois adoré notre séance.\n\nJulie est de très bon conseil, chaleureuse et bienveillante, je recommande à 100% 🫶🏻",
                        avatar: "C"
                    },
                    {
                        name: "Mélanie M.",
                        rating: 5,
                        text: "Julie m'a tatoué un magnifique colibri.\n\nElle est à l'écoute et créative.\n\nJe fais en ce moment mon deuxième tatouage. C'est dire que je suis ravie.",
                        avatar: "M"
                    },
                    {
                        name: "Gwen D.",
                        rating: 5,
                        text: "Vraiment une tatoueuse au top ! Je recommande vivement !\n\nUn professionnalisme exemplaire, je me fais tatouer par elle depuis 1 an et je n'ai jamais été déçue par ses tatouages !\n\nUn grand merci à elle pour sa patience.",
                        avatar: "G"
                    }
                ].map((review, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        whileHover={{ y: -5 }}
                        className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl shadow-lg border border-gray-100"
                    >
                        {/* Header de l'avis */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                                {/* Avatar */}
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                                    {review.avatar}
                                </div>
                                <div>
                                    <h4 className="font-semibold text-zinc-900">{review.name}</h4>
                                </div>
                            </div>
                            {/* Logo Google */}
                            <div className="w-6 h-6">
                                <svg viewBox="0 0 24 24" className="w-full h-full">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                            </div>
                        </div>

                        {/* Étoiles */}
                        <div className="flex items-center gap-1 mb-3">
                            {[...Array(review.rating)].map((_, i) => (
                                <span key={i} className="text-yellow-400 text-lg">★</span>
                            ))}
                        </div>

                        {/* Texte de l'avis */}
                        <p className="text-zinc-700 leading-relaxed whitespace-pre-line">
                            {review.text}
                        </p>
                    </motion.div>
                ))}
            </div>

            {/* CTA vers Google Reviews */}
            <motion.div 
                className="text-center mt-16"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
            >
                <a
                    href="https://maps.app.goo.gl/3tupDzt3YMsPgiDy5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-white border-2 border-gray-200 rounded-full text-lg font-semibold text-zinc-900 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 group"
                >
                    <svg viewBox="0 0 24 24" className="w-6 h-6">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>Lire tous les avis sur Google</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                </a>
            </motion.div>
        </div>
    </FadeIn>

                    
    {/* Section Contact */}
    <FadeIn sectionId="contact" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* En-tête */}
            <div className="text-center mb-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold uppercase tracking-wider mb-4">
                        Contactez-nous
                    </span>
                    <h2 className="title-font text-4xl md:text-5xl font-bold text-zinc-900 mb-4">
                        Restons en Contact
                    </h2>
                    <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
                        Prêt(e) à concrétiser ton projet ? Plusieurs façons de nous joindre.
                    </p>
                </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                {/* Colonne Contact */}
                <motion.div
                    className="lg:col-span-1 bg-white p-8 rounded-2xl shadow-xl border border-gray-100"
                    variants={itemVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <Mail size={24} className="text-white" />
                        </div>
                        <h3 className="title-font text-2xl font-bold text-zinc-900">Nous Contacter</h3>
                    </div>
                    
                    <div className="space-y-5 mb-8">
                        {/* Téléphone */}
                        <div className="group">
                            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">
                                Téléphone
                            </label>
                            <a 
                                href="tel:0668346502" 
                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-all duration-300 group-hover:scale-[1.02]"
                            >
                                <Phone size={18} className="text-indigo-600 flex-shrink-0" />
                                <span className="text-zinc-700 group-hover:text-indigo-700 font-medium">
                                    06.68.34.65.02
                                </span>
                            </a>
                        </div>

                        {/* Email */}
                        <div className="group">
                            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">
                                Email
                            </label>
                            <a 
                                href={`mailto:${CONTACT_INFO.email}`} 
                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-all duration-300 group-hover:scale-[1.02]"
                            >
                                <Mail size={18} className="text-indigo-600 flex-shrink-0" />
                                <span className="text-zinc-700 group-hover:text-indigo-700 font-medium">
                                    {CONTACT_INFO.email}
                                </span>
                            </a>
                        </div>

                        {/* Instagram */}
                        <div className="group">
                            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">
                                Instagram
                            </label>
                            <a 
                                href={CONTACT_INFO.instagram} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg hover:from-purple-100 hover:to-pink-100 transition-all duration-300 group-hover:scale-[1.02]"
                            >
                                <Instagram size={18} className="text-pink-600 flex-shrink-0" />
                                <span className="text-zinc-700 group-hover:text-pink-700 font-medium">
                                    @lumine_tattoostudio
                                </span>
                            </a>
                        </div>

                        {/* Adresse */}
                        <div className="group">
                            <label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">
                                Adresse
                            </label>
                            <a 
                                href={CONTACT_INFO.gmapsLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-all duration-300 group-hover:scale-[1.02]"
                            >
                                <MapPin size={18} className="text-indigo-600 flex-shrink-0 mt-0.5" />
                                <span className="text-zinc-700 group-hover:text-indigo-700 font-medium leading-relaxed">
                                    {CONTACT_INFO.address}
                                </span>
                            </a>
                        </div>
                    </div>

                    {/* CTA Principal */}
                    <motion.a 
                        href={CONTACT_INFO.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl text-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <Instagram size={22} />
                        Prendre Rendez-vous
                    </motion.a>
                </motion.div>

                {/* Colonne Horaires */}
                <motion.div
                    className="lg:col-span-1 bg-white p-8 rounded-2xl shadow-xl border border-gray-100"
                    variants={itemVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <Clock size={24} className="text-white" />
                        </div>
                        <h3 className="title-font text-2xl font-bold text-zinc-900">Horaires</h3>
                    </div>

                    <div className="space-y-3">
                        {SCHEDULE.map((item, index) => (
                            <div 
                                key={index} 
                                className={`flex justify-between items-center p-3 rounded-lg transition-colors ${
                                    item.hours === 'Fermé' 
                                        ? 'bg-red-50' 
                                        : 'bg-gray-50 hover:bg-indigo-50'
                                }`}
                            >
                                <span className="font-semibold text-zinc-900">{item.day}</span>
                                <span className={`font-medium ${
                                    item.hours === 'Fermé' 
                                        ? 'text-red-600' 
                                        : 'text-indigo-700'
                                }`}>
                                    {item.hours}
                                </span>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Colonne Carte */}
                <motion.div
                    className="lg:col-span-1 rounded-2xl overflow-hidden shadow-xl border border-gray-100"
                    variants={itemVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <div className="relative h-full min-h-[400px]">
                        {/* Badge sur la carte */}
                        <div className="absolute top-4 left-4 z-10 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                            <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                                <MapPin size={16} className="text-indigo-600" />
                                Lumine Tattoo Studio
                            </p>
                        </div>

                        {/* Carte Google Maps */}
                        <iframe
                            title="Localisation du Lumine Tattoo Studio"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2529.5636043132644!2d3.1118953157522276!3d50.6690412799391!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c32729e99c351b%3A0x6a21c210e74f4b23!2s9%20Rue%20Francisco%20Ferrer%2C%2059290%20Wasquehal!5e0!3m2!1sfr!2sfr!4v1642876800000!5m2!1sfr!2sfr"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="absolute inset-0"
                        ></iframe>

                        {/* Bouton itinéraire */}
                        <a
                            href={CONTACT_INFO.gmapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg hover:bg-white transition-all duration-300 flex items-center justify-center gap-2 font-semibold text-zinc-900 hover:scale-105"
                        >
                            <MapPin size={18} className="text-indigo-600" />
                            Obtenir l'itinéraire
                        </a>
                    </div>
                </motion.div>
            </div>

            {/* Bloc info pratique en bas */}
            <motion.div
                className="max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
            >
<div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 p-8 rounded-2xl border border-indigo-100 text-center">
    <h4 className="text-xl font-bold text-zinc-900 mb-3">
        📍 Facilement Accessible
    </h4>
    <p className="text-zinc-700 leading-relaxed">
        Situé à Wasquehal, à proximité de <span className="font-semibold">Croix, Marcq-en-Barœul et Lille</span>. 
        Parking gratuit disponible à proximité. Accessible en transport en commun :<br /><br />
        <span className="font-semibold">Métro :</span> Wasquehal, Hotel de Ville<br />
        <span className="font-semibold">Bus :</span> Wasquehal, Hotel de Ville<br />
        <span className="font-semibold">Station V'Lille :</span> à proximité
    </p>
</div>
            </motion.div>
        </div>
    </FadeIn>
    </div>
                </main>

                {/* Footer */}
                <footer className="bg-black text-white pt-12 pb-8 mt-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row justify-between items-center md:items-start border-b border-gray-800 pb-8">
                            {/* Logo et Adresse */}
                            <div className="mb-6 md:mb-0 text-center md:text-left">
                                <img src={logoUrl} alt="Lumine Logo" className="h-12 object-contain mx-auto md:mx-0 mb-3" loading="lazy" />
                                <p className="text-sm text-gray-400">9 Rue Francisco Ferrer</p>
                                <p className="text-sm text-gray-400">59290 Wasquehal</p>
                                <a href={CONTACT_INFO.gmapsLink} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors flex items-center justify-center md:justify-start mt-2">
                                    <MapPin size={16} className="mr-1" />
                                    Voir sur la carte
                                </a>
                            </div>

                            {/* Navigation Footer */}
                            <div className="flex flex-wrap justify-center md:justify-start gap-x-10 gap-y-4 mb-6 md:mb-0">
                                {['accueil', 'studio', 'portfolio', 'rdv','preparation','soins','avis', 'contact'].map((id) => (
                                    <button
                                        key={`footer-${id}`}
                                        className="text-gray-300 hover:text-white font-light text-sm transition-colors uppercase"
                                        onClick={() => scrollToSection(id)}
                                    >
                                        {id.charAt(0).toUpperCase() + id.slice(1)}
                                    </button>
                                ))}
                            </div>

                            {/* Liens Sociaux & CTA */}
                            <div className="flex flex-col items-center md:items-end space-y-4">
                                <div className="flex space-x-4">
                                    <a href={CONTACT_INFO.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-pink-500 transition-colors" aria-label="Instagram">
                                        <Instagram size={28} />
                                    </a>
                                    <a href={`mailto:${CONTACT_INFO.email}`} className="text-gray-300 hover:text-indigo-500 transition-colors" aria-label="Email">
                                        <Mail size={28} />
                                    </a>
                                </div>
<div className="mt-4">
    <button
        onClick={() => scrollToSection('rdv')}
        className="px-6 py-3 bg-indigo-600 text-white rounded-full text-md font-semibold hover:bg-indigo-700 transition-colors transform hover:scale-105 shadow-lg"
    >
        <span>Prendre Rendez-vous</span>
    </button>
</div>
                            </div>
                        </div>

                        <div className="text-center text-sm text-gray-600 pt-8">
                            <p className="font-light">© 2025 Lumine Tattoo Studio – Tous droits réservés.</p>
                            <p className="mt-1 text-xs font-light">Tatoueur à Wasquehal, proche de Croix, Marcq-en-Barœul et Lille. Hauts-de-France.</p>
                        </div>
                    </div>
                </footer>

                {/* Lightbox Modal */}
                <AnimatePresence>
                    {lightbox && (
                        <Lightbox
                            src={lightbox.src}
                            alt={lightbox.alt}
                            onClose={() => setLightbox(null)}
                        />
                    )}
                </AnimatePresence>

                {/* Back to Top Button - Black button with white icon */}
                <AnimatePresence>
                    {showScrollTop && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => scrollToSection('accueil')}
                            className={`fixed bottom-6 right-6 p-4 bg-black text-white rounded-full shadow-lg hover:bg-gray-800 transition-all duration-300 z-50`}
                            aria-label="Retour en haut de page"
                        >
                            <ArrowUp size={24} />
                        </motion.button>
                    )}
                </AnimatePresence>
            </div>
            
        );
    }
